import { BaseEdgeFunctionClient } from '../shared/base-client';
import { OpenAIConfig } from './types';

export interface ThreadedTextGenerationRequest {
  type: 'create-thread' | 'ai-text-generation' | 'page-content-generation';
  thread_id?: string;
  mode?: 'generate' | 'enhance' | 'edit';
  prompt?: string;
  selectedText?: string;
  contentType?: string;
  contentLength?: number;
  editType?: string;
  enhanceType?: string;
  appName?: string;
  functionName?: string;
  context?: {
    pageTitle?: string;
    workbookTitle?: string;
    writingStyle?: string;
    appName?: string;
    functionName?: string;
    researchSummaries?: Array<{
      title: string;
      summary: string;
      source?: string;
    }>;
  };
  // Page content generation specific
  pageTitle?: string;
  pageType?: string;
  workbookContext?: string;
  writingStyle?: string;
  initial_request?: string;
  researchSummaries?: Array<{
    title: string;
    summary: string;
  }>;
}

export interface ThreadedTextGenerationResponse {
  thread_id?: string;
  content?: string;
  error?: string;
}

export class ThreadedTextGenerationClient extends BaseEdgeFunctionClient {
  constructor(config: OpenAIConfig = {}) {
    super({
      ...config,
      retries: 0  // CRITICAL: Disable retries for threaded operations - each call creates a new OpenAI run
    });
  }

  /**
   * Create a new OpenAI thread
   */
  async createThread(): Promise<string> {
    try {
      const response = await this.invokeEdgeFunction<ThreadedTextGenerationResponse>(
        'threaded-text-generation',
        { type: 'create-thread' }
      );

      if (!response.thread_id) {
        throw new Error('No thread ID returned from function');
      }

      return response.thread_id;
    } catch (error) {
      this.handleError(error, 'Failed to create OpenAI thread');
    }
  }

  /**
   * Generate AI text using threaded conversation
   */
  async generateAIText(request: Omit<ThreadedTextGenerationRequest, 'type'>): Promise<string> {
    try {
      const response = await this.invokeEdgeFunction<ThreadedTextGenerationResponse>(
        'threaded-text-generation',
        {
          type: 'ai-text-generation',
          ...request,
          appName: request.appName || 'Content Generator',
          functionName: request.functionName || 'AI Text Generation'
        }
      );

      if (response.error) {
        throw new Error(response.error);
      }

      if (!response.content) {
        throw new Error('No content returned from function');
      }

      return response.content;
    } catch (error) {
      this.handleError(error, 'AI text generation failed');
    }
  }

  /**
   * Generate page content using threaded conversation
   */
  async generatePageContent(request: Omit<ThreadedTextGenerationRequest, 'type'>): Promise<string> {
    try {
      const response = await this.invokeEdgeFunction<ThreadedTextGenerationResponse>(
        'threaded-text-generation',
        {
          type: 'page-content-generation',
          ...request,
          appName: request.appName || 'Content Generator',
          functionName: request.functionName || 'Page Generation'
        }
      );

      if (response.error) {
        throw new Error(response.error);
      }

      if (!response.content) {
        throw new Error('No content returned from function');
      }

      return response.content;
    } catch (error) {
      this.handleError(error, 'Page content generation failed');
    }
  }
} 